import {
  CORP_HOMEPAGE_SUBTITLE,
  CORP_HOMEPAGE_TITLE,
  isCaponeTenant,
  isCorpTenant,
  useUserContext,
} from "@capone/common";
import { HomePageHotelSearch } from "@capone/portal";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import {
  CorporateTypography,
  TravelerWorkflowDialog,
  useDeviceTypes,
} from "halifax";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CallState,
  cardsToShowWithPriorityOnRewardsBanner,
  CorpSessionInfo,
  CustomEvents,
  EditTravelerModalState,
  Funnel,
  IPerson,
  ModalNames,
  RewardsAccount,
  SavedTravelersProperties,
  SelfServeEvents,
  SessionInfo,
  Tenant,
  WalletDetailsResponse,
} from "redmond";
import { trackEvent } from "../../api/v1/trackEvent";
import {
  deleteUserPassenger,
  fetchCorpUserPassengers,
  setSessionInfoInStore,
  updateUserPassenger,
} from "../../app/actions";
import { getUserPassengers } from "../../app/selectors";
import {
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  CONTROL,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  HOTEL_CROSS_SELL_V3,
  HOTEL_CROSS_SELL_V3_VARIANTS,
  HOTELS_HOMEPAGE_CROSS_SELL_EXPERIMENT,
  RECENTLY_VIEWED_DESKTOP_P0_EXPERIMENT,
  RECENTLY_VIEWED_DESKTOP_P0_VARIANTS,
  useExperiments,
} from "../../context/experiments";
import { useSeenModalUpdater } from "../../hooks/useSeenModalUpdater";
import config from "../../utils/config";
import { CrossSellProps } from "../Body/cross-sell-helpers";
import { RecentlyViewedAndSearchedProps } from "../Body/recently-viewed-and-searched-helpers";
import { CorpHomePageBottomSection } from "../capone-corporate/CorpHomePageBottomSection/component";
import { CorpHomePageTopSection } from "../capone-corporate/CorpHomePageTopSection/component";
import { CarSearch } from "../CarSearch";
import { HomepageAnnualTravelCredit } from "../HomepageAnnualTravelCredit";
import { HomepageHotelCrossSell } from "../HomepageHotelCrossSell";
import { LandingPageType, MobileLandingPage } from "../MobileLandingPage";
import { RecentlyViewedAndSearched } from "../RecentlyViewedAndSearched";
import styles from "./styles.module.scss";
import {
  EARN_ENHANCEMENT_TITLE,
  PARADISE_TITLE,
  STAYS_SUBTITLE,
  SUBTITLE,
  TITLE,
} from "./textConstants";

type HopePageType = {
  type: LandingPageType;
  rewardsAccounts: RewardsAccount[];
  selectedRewardsAccountId: string | null;
  fetchRewardsAccountsCallState: CallState;
  walletDetails: WalletDetailsResponse | null;
  crossSellProps: CrossSellProps | null;
  recentlyViewedAndSearchedProps: RecentlyViewedAndSearchedProps | null;
  recentlyViewedAndSearchedLoaded: boolean;
};
/**
 * Component to render all home page products
 * TODO: implement funnel handling (cars, flights, hotels, etc)
 */
export const HomePage = ({
  type,
  rewardsAccounts,
  selectedRewardsAccountId,
  fetchRewardsAccountsCallState,
  walletDetails,
  crossSellProps,
  recentlyViewedAndSearchedProps,
  recentlyViewedAndSearchedLoaded,
}: HopePageType) => {
  const { matchesMobile } = useDeviceTypes();

  const { sessionInfo } = useUserContext(config.TENANT);
  const [localSessionInfo, setLocalSessionInfo] = useState<
    CorpSessionInfo | SessionInfo | undefined
  >(sessionInfo);

  const expState = useExperiments();

  const dispatch = useDispatch();

  const travelers = useSelector(getUserPassengers);

  const isHotelCrossSellExperiment =
    getExperimentVariant(
      expState.experiments,
      HOTELS_HOMEPAGE_CROSS_SELL_EXPERIMENT
    ) === AVAILABLE;

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const isHotelCrossSellV3Enabled =
    getExperimentVariantCustomVariants(
      expState.experiments,
      HOTEL_CROSS_SELL_V3,
      HOTEL_CROSS_SELL_V3_VARIANTS
    ) !== CONTROL;

  const isRecentlyViewedDesktopP0Experiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      RECENTLY_VIEWED_DESKTOP_P0_EXPERIMENT,
      RECENTLY_VIEWED_DESKTOP_P0_VARIANTS
    ) !== CONTROL;

  const funnel = useMemo(() => {
    switch (type) {
      case LandingPageType.FLIGHTS:
        return Funnel.Air;
      case LandingPageType.HOTELS:
        return Funnel.Lodging;
      case LandingPageType.STAYS:
        return Funnel.Homes;
      case LandingPageType.CARS:
        return Funnel.Ground;
      case LandingPageType.PREMIUM_STAYS:
        return Funnel.Homes;
      case LandingPageType.PACKAGES:
        return Funnel.Lodging;
      case LandingPageType.EXPERIENCES:
        return Funnel.Experiences;
      default:
        return undefined;
    }
  }, [type]);

  const showCrossSellV3UI =
    isHotelCrossSellV3Enabled && !!crossSellProps?.tysonsOffer;

  const largestValueAccount = React.useMemo(
    () =>
      [...rewardsAccounts].sort((prev, current) => {
        const earnMultiplier = (account: RewardsAccount) =>
          prev.earn.flightsMultiplier && current.earn.flightsMultiplier
            ? account.earn.flightsMultiplier
            : account.earn.carsMultiplier ?? -1;
        if (earnMultiplier(current) === earnMultiplier(prev)) {
          return cardsToShowWithPriorityOnRewardsBanner.includes(
            current.productDisplayName.toLowerCase()
          )
            ? 1
            : -1;
        }
        return earnMultiplier(current) - earnMultiplier(prev);
      })[0],
    [rewardsAccounts]
  );

  const hasEarnToDisplay =
    !!largestValueAccount && !!largestValueAccount.earn.carsMultiplier;

  const getTitleToDisplay = (
    tenant: Tenant,
    primaryEarnRate: string | number,
    secondaryEarnRate?: string | number
  ) => {
    if (isCorpTenant(tenant)) {
      return (
        <CorporateTypography variant="h1">
          {CORP_HOMEPAGE_TITLE}
        </CorporateTypography>
      );
    }
    if (
      fetchRewardsAccountsCallState === CallState.Success &&
      hasEarnToDisplay
    ) {
      return EARN_ENHANCEMENT_TITLE(
        primaryEarnRate,
        largestValueAccount.productDisplayName,
        largestValueAccount.rewardsBalance.currencyDescription ??
          largestValueAccount.rewardsBalance.currency,
        type,
        secondaryEarnRate
      );
    }
    if (
      fetchRewardsAccountsCallState === CallState.Success ||
      fetchRewardsAccountsCallState === CallState.Failed
    ) {
      if (
        largestValueAccount?.productDisplayName
          .toLowerCase()
          .includes("paradise")
      ) {
        return <span className="font-regular">{PARADISE_TITLE}</span>;
      }
      return <span className="font-regular">{TITLE}</span>;
    }
    return <Skeleton className="title-loading" />;
  };

  const getSubtitleToDisplay = (tenant: Tenant) => {
    if (isCorpTenant(tenant)) {
      return (
        <CorporateTypography variant="h5" fontWeight="light">
          {CORP_HOMEPAGE_SUBTITLE}
        </CorporateTypography>
      );
    }
    if (
      fetchRewardsAccountsCallState === CallState.Success ||
      fetchRewardsAccountsCallState === CallState.Failed
    ) {
      if (type === LandingPageType.HOTELS || type === LandingPageType.STAYS) {
        return STAYS_SUBTITLE;
      }
      return SUBTITLE;
    }
    return <Skeleton className="subtitle-loading" />;
  };

  const search = useMemo(() => {
    switch (type) {
      case LandingPageType.HOTELS:
      case LandingPageType.STAYS:
        return (
          <div className={styles["car-search-container"]}>
            <div className={styles["car-search-heading"]}>
              {getTitleToDisplay(
                config.TENANT,
                largestValueAccount.earn.hotelsMultiplier,
                largestValueAccount.earn.homesMultiplier
              )}
            </div>
            <div className={styles["car-search-subtitle"]}>
              {getSubtitleToDisplay(config.TENANT)}
            </div>
            <HomePageHotelSearch className={styles.search} />
          </div>
        );
      case LandingPageType.CARS: {
        return (
          <div
            className={clsx(styles["car-search-container"], {
              [styles["capone-corporate"]]: isCorpTenant(config.TENANT),
            })}
          >
            <div className={styles["car-search-heading"]}>
              {getTitleToDisplay(
                config.TENANT,
                largestValueAccount.earn.carsMultiplier
              )}
            </div>
            <div className={styles["car-search-subtitle"]}>
              {getSubtitleToDisplay(config.TENANT)}
            </div>
            <CarSearch className={styles.search} />
          </div>
        );
      }
      case LandingPageType.FLIGHTS:
      case LandingPageType.PREMIUM_STAYS:
      case LandingPageType.PACKAGES:
      case LandingPageType.EXPERIENCES:
      default:
        console.error("Homepage funnel not implemented");
        return null;
    }
  }, [type]);

  const [editTravelersOpen, setEditTravelersOpen] = useState(
    EditTravelerModalState.closed
  );

  const userRoles =
    sessionInfo && "corporateInfo" in sessionInfo
      ? (sessionInfo as CorpSessionInfo).corporateInfo.role
      : [];

  const defaultTravelerId =
    sessionInfo && "corporateInfo" in sessionInfo
      ? (sessionInfo as CorpSessionInfo).corporateInfo.defaultTravelerId
      : "";

  const hasSeenNfuInfoModal =
    (sessionInfo &&
      "corporateInfo" &&
      (sessionInfo as CorpSessionInfo).corporateInfo?.hasSeenModalMap?.[
        ModalNames.NFU_IN_PRODUCT_AWARENESS_PROFILES
      ]) ??
    false;

  const closeEditTravelersModal = useCallback(() => {
    setEditTravelersOpen(EditTravelerModalState.closed);
  }, []);

  const onEditTravelersStateChange = useCallback((ev: any) => {
    const newEditModalState =
      (ev as CustomEvent).detail ?? EditTravelerModalState.closed;

    setEditTravelersOpen(newEditModalState);
  }, []);

  const handleAddPass = useCallback(() => {
    !isCorpTenant(config.TENANT) &&
      trackEvent({
        eventName: SelfServeEvents.ClickSavedTravelersAddNew,
        properties: {
          url: window.location.pathname,
        },
      });
  }, []);

  useEffect(() => {
    if (isCorpTenant(config.TENANT) && rewardsAccounts.length > 0) {
      dispatch(fetchCorpUserPassengers());
    }
  }, [rewardsAccounts]);

  useEffect(() => {
    document.addEventListener(
      CustomEvents.editTravelersStateChange,
      onEditTravelersStateChange
    );

    return () => {
      document.removeEventListener(
        CustomEvents.editTravelersStateChange,
        onEditTravelersStateChange
      );
    };
  }, []);

  const isOpenTravelerModal = useMemo(
    () =>
      (editTravelersOpen === EditTravelerModalState.ground &&
        type == LandingPageType.CARS) ||
      (editTravelersOpen === EditTravelerModalState.hotel &&
        type == LandingPageType.HOTELS),
    [type, editTravelersOpen]
  );

  useEffect(() => {
    if (localSessionInfo) {
      dispatch(setSessionInfoInStore(localSessionInfo));
    }
  }, [localSessionInfo]);

  return matchesMobile ? (
    <MobileLandingPage
      type={type}
      rewardsAccounts={rewardsAccounts}
      selectedRewardsAccountId={selectedRewardsAccountId}
      fetchRewardsAccountsCallState={fetchRewardsAccountsCallState}
      recentlyViewedAndSearchedLoaded={recentlyViewedAndSearchedLoaded}
    />
  ) : (
    <>
      {search}
      <CorpHomePageTopSection
        sessionInfo={sessionInfo}
        trackEvent={trackEvent}
        funnel={funnel}
        accounts={rewardsAccounts}
        selectedAccountReferenceId={selectedRewardsAccountId}
      />
      {walletDetails && (
        <HomepageAnnualTravelCredit
          firstName={sessionInfo.userInfo.firstName}
          rewardsAccounts={rewardsAccounts}
          walletDetails={walletDetails}
          isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
        />
      )}

      {isHotelCrossSellExperiment && showCrossSellV3UI && crossSellProps ? (
        <HomepageHotelCrossSell
          searchLocationResult={crossSellProps.searchLocationResult}
          fromDate={crossSellProps.fromDate}
          untilDate={crossSellProps.untilDate}
          adultCount={crossSellProps.adultCount}
          childAges={crossSellProps.childAges}
          largestValueAccount={crossSellProps.largestValueAccount}
          latency={crossSellProps.latency}
          bannerText={crossSellProps.bannerText}
          tysonsOffer={crossSellProps.tysonsOffer}
        />
      ) : null}

      {(() => {
        switch (true) {
          case !!recentlyViewedAndSearchedProps &&
            isRecentlyViewedDesktopP0Experiment &&
            !!rewardsAccounts.length:
            return isCaponeTenant(config.TENANT) ? (
              <RecentlyViewedAndSearched
                recentSearchesAndViews={
                  recentlyViewedAndSearchedProps!.recentSearchesAndViews
                }
                largestValueAccount={
                  recentlyViewedAndSearchedProps!.largestValueAccount
                }
                latency={recentlyViewedAndSearchedProps!.latency}
                negativeMargin={recentlyViewedAndSearchedProps!.negativeMargin}
                loading={!recentlyViewedAndSearchedLoaded}
                showSkeleton={false}
              />
            ) : (
              <div>
                <RecentlyViewedAndSearched
                  recentSearchesAndViews={
                    recentlyViewedAndSearchedProps!.recentSearchesAndViews
                  }
                  largestValueAccount={
                    recentlyViewedAndSearchedProps!.largestValueAccount
                  }
                  latency={recentlyViewedAndSearchedProps!.latency}
                  negativeMargin={
                    recentlyViewedAndSearchedProps!.negativeMargin
                  }
                  loading={!recentlyViewedAndSearchedLoaded}
                  showSkeleton={isCorpTenant(config.TENANT)}
                />
              </div>
            );
          default:
            return null;
        }
      })()}

      {isHotelCrossSellExperiment && !showCrossSellV3UI && crossSellProps ? (
        <HomepageHotelCrossSell
          searchLocationResult={crossSellProps.searchLocationResult}
          fromDate={crossSellProps.fromDate}
          untilDate={crossSellProps.untilDate}
          adultCount={crossSellProps.adultCount}
          childAges={crossSellProps.childAges}
          largestValueAccount={crossSellProps.largestValueAccount}
          latency={crossSellProps.latency}
          bannerText={crossSellProps.bannerText}
          tysonsOffer={crossSellProps.tysonsOffer}
        />
      ) : null}
      <CorpHomePageBottomSection sessionInfo={sessionInfo} />
      <TravelerWorkflowDialog
        tenant={config.TENANT}
        showHotelLoyaltySection
        disableDriversLicenseValidation
        disallowSelect
        displayModalSubtitle
        onAddPass={handleAddPass}
        showAdditionalInfoSection
        showDriverLicenseSection
        showFrequentFlyerSection
        showGenderField
        showNationalityField
        buttonClassName="b2b"
        dialogProps={{
          className: "cars-edit-traveler",
          onBackdropClick: closeEditTravelersModal,
          onClose: closeEditTravelersModal,
          open: isOpenTravelerModal,
        }}
        errorMessage="Add or choose one traveler to continue."
        fullScreenWithBanner={matchesMobile}
        handleDeletePassenger={(personId: string) => {
          dispatch(deleteUserPassenger({ personId }));
        }}
        handleUpdatePassenger={(
          person: IPerson,
          hideProfileAction,
          entryPoint
        ) => {
          const frequentFlyerProgramsTotal = Reflect.ownKeys(
            person.frequentFlyer
          ).length;

          !isCorpTenant(config.TENANT) &&
            trackEvent({
              eventName: SelfServeEvents.ClickSavedTravelersSaveTraveler,
              properties: {
                url: window.location.pathname,
                frequent_flyer_program_added: frequentFlyerProgramsTotal > 0,
                num_frequent_flyer_programs: frequentFlyerProgramsTotal,
              } satisfies SavedTravelersProperties,
            });
          dispatch(
            updateUserPassenger(
              { person, hideProfileAction },
              defaultTravelerId,
              entryPoint
            )
          );
        }}
        isMobile={matchesMobile}
        travelers={travelers}
        titles={{}}
        dialogContentProps={{}}
        userRoles={userRoles}
        trackEvent={trackEvent}
        defaultTravelerId={defaultTravelerId}
        hasSeenNfuInfo={hasSeenNfuInfoModal}
        handleSeenNfuInfoModal={useSeenModalUpdater(
          sessionInfo,
          setLocalSessionInfo
        )}
      />
    </>
  );
};
